// src/utils/get-brazilian-state-uf.js
// *****************************************************************************
// BRAZILIAN STATE UF UTILITIES
// *****************************************************************************
// Utility functions for working with Brazilian state codes (UF)
// Converts state names to their official two-letter abbreviations
// Handles normalized input with accent removal and case insensitivity
// *****************************************************************************

// *****************************************************************************
// CONSTANTS
// *****************************************************************************

/**
 * Map of normalized state names to UF codes
 */
const STATES_MAP = {
  acre: 'AC',
  alagoas: 'AL',
  amapa: 'AP',
  amazonas: 'AM',
  bahia: 'BA',
  ceara: 'CE',
  'distrito federal': 'DF',
  'espirito santo': 'ES',
  goias: 'GO',
  maranhao: 'MA',
  'mato grosso': 'MT',
  'mato grosso do sul': 'MS',
  'minas gerais': 'MG',
  para: 'PA',
  paraiba: 'PB',
  parana: 'PR',
  pernambuco: 'PE',
  piaui: 'PI',
  'rio de janeiro': 'RJ',
  'rio grande do norte': 'RN',
  'rio grande do sul': 'RS',
  rondonia: 'RO',
  roraima: 'RR',
  'santa catarina': 'SC',
  'sao paulo': 'SP',
  sergipe: 'SE',
  tocantins: 'TO',
};

/**
 * Array of all valid UF codes
 */
const VALID_UFS = Object.values(STATES_MAP);

// *****************************************************************************
// PUBLIC METHODS
// *****************************************************************************

/**
 * Get Brazilian state UF code from state name or UF
 * @param {string} input - State name or UF code
 * @returns {string|null} Two-letter UF code in uppercase or null if not found
 * @example
 * getBrazilianStateUf('São Paulo') // 'SP'
 * getBrazilianStateUf('sp') // 'SP'
 * getBrazilianStateUf('Minas Gerais') // 'MG'
 */
const getBrazilianStateUf = input => {
  if (!input || typeof input !== 'string') return null;

  const normalized = normalizeString(input);

  // Check if already a valid UF
  const uf = normalized.toUpperCase();
  if (VALID_UFS.includes(uf)) return uf;

  // Look up in states map
  return STATES_MAP[normalized] || null;
};

/**
 * Get Brazilian state name from UF code
 * @param {string} uf - Two-letter UF code
 * @returns {string|null} State name with proper capitalization or null if not found
 * @example
 * getStateNameFromUF('SP') // 'São Paulo'
 * getStateNameFromUF('MG') // 'Minas Gerais'
 */
const getStateNameFromUF = uf => {
  if (!uf || typeof uf !== 'string') return null;

  const upperUF = uf.toUpperCase();

  // Find the state name by UF
  for (const [stateName, stateUF] of Object.entries(STATES_MAP)) {
    if (stateUF === upperUF) {
      return formatStateName(stateName);
    }
  }

  return null;
};

/**
 * Validate if a string is a valid Brazilian state UF
 * @param {string} uf - String to validate
 * @returns {boolean} True if valid UF code
 */
const isValidUF = uf => {
  if (!uf || typeof uf !== 'string') return false;
  return VALID_UFS.includes(uf.toUpperCase());
};

/**
 * Get all Brazilian states as array of objects
 * @returns {Array<{name: string, uf: string}>} Array of state objects
 */
const getAllStates = () => {
  return Object.entries(STATES_MAP).map(([name, uf]) => ({
    name: formatStateName(name),
    uf,
  }));
};

/**
 * Get all Brazilian UF codes
 * @returns {string[]} Array of UF codes
 */
const getAllUFs = () => {
  return [...VALID_UFS];
};

// *****************************************************************************
// EXPORTS
// *****************************************************************************

export {
  getBrazilianStateUf,
  getStateNameFromUF,
  isValidUF,
  getAllStates,
  getAllUFs,
};

// *****************************************************************************
// HELPERS
// *****************************************************************************

/**
 * Normalize string by removing accents and converting to lowercase
 * @param {string} str - String to normalize
 * @returns {string} Normalized string
 */
function normalizeString(str = '') {
  return str
    .normalize('NFD') // Separate accents
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .toLowerCase()
    .trim();
}

/**
 * Format state name with proper capitalization
 * @param {string} stateName - Normalized state name
 * @returns {string} Formatted state name
 */
function formatStateName(stateName) {
  const specialCases = {
    'distrito federal': 'Distrito Federal',
    'espirito santo': 'Espírito Santo',
    'mato grosso': 'Mato Grosso',
    'mato grosso do sul': 'Mato Grosso do Sul',
    'minas gerais': 'Minas Gerais',
    'rio de janeiro': 'Rio de Janeiro',
    'rio grande do norte': 'Rio Grande do Norte',
    'rio grande do sul': 'Rio Grande do Sul',
    'santa catarina': 'Santa Catarina',
    'sao paulo': 'São Paulo',
  };

  if (specialCases[stateName]) {
    return specialCases[stateName];
  }

  // Default: capitalize first letter
  return stateName.charAt(0).toUpperCase() + stateName.slice(1);
}
